import React, {
  forwardRef,
  Ref,
  useState,
  useEffect,
  ChangeEvent,
  Fragment,
} from 'react';
import { FormSelect } from '@spotify-internal/encore-web';
import { FieldController } from 'client/shared/FieldController';
import {
  FieldSelectProps,
  NestedOptionType,
  OptGroupType,
  OptionType,
  OptionVariant,
  Variant,
  ControlledFieldSelectProps,
} from './types';

function getNestedOptionSelected(selectElement: HTMLSelectElement) {
  const { selectedOptions } = selectElement;
  if (selectedOptions.length === 0) return false;
  return selectedOptions[0].getAttribute('data-nested-option') === 'true';
}

function getSubOptions(options: OptionVariant[]) {
  let allSubOptions: OptionType[] = [];
  options.forEach(option => {
    const optionType = getOptionType(option);
    if (optionType === 'nested-option') {
      const { subOptions } = option as NestedOptionType;
      allSubOptions = [...allSubOptions, ...subOptions];
    }
  });
  return allSubOptions;
}

export const FieldSelect = forwardRef(
  (
    {
      id,
      name,
      value,
      options,
      placeholder,
      onChange,
      error,
      containerClassName,
      ...rest
    }: FieldSelectProps,
    ref: Ref<HTMLSelectElement>
  ) => {
    // `isValueNestedOption` allows us to adjust the padding on the select element
    // based on the seleted value type. We display nested options with three spaces
    // prepended in order to get the visual of it being nested in the select
    // dropdown, side effect here is when it's displayed in the select input,
    // you see the spaces. removing the padding will make this look not weird
    const [isValueNestedOption, setIsValueNestedOption] = useState<
      boolean | null
    >(null);
    useEffect(() => {
      if (isValueNestedOption === null && options && options.length > 0) {
        setIsValueNestedOption(
          getSubOptions(options).some(option => option.value === value)
        );
      }
    }, [value, options, isValueNestedOption]);
    function handleChange(e: ChangeEvent<HTMLSelectElement>) {
      setIsValueNestedOption(getNestedOptionSelected(e.currentTarget));
      if (onChange) {
        onChange(e);
      }
    }

    return (
      <FormSelect
        {...rest}
        error={error?.message}
        ref={ref}
        id={id}
        name={name}
        data-cy={name}
        value={value}
        onChange={handleChange}
        style={
          isValueNestedOption
            ? {
                paddingLeft: 0,
              }
            : {}
        }
        data-value={value}
        className={containerClassName}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options !== undefined &&
          options.map(option => {
            const { label: displayLabel } = option;
            const optionType = getOptionType(option);
            // eslint-disable-next-line default-case
            switch (optionType) {
              case 'option': {
                const { value: optionValue } = option as OptionType;
                return (
                  <Option
                    key={`select-option-${id}-${optionValue}-${displayLabel}`}
                    value={optionValue}
                    label={displayLabel}
                  />
                );
              }
              case 'optgroup': {
                const { subOptions } = option as OptGroupType;
                return (
                  <OptGroup
                    key={`select-optgroup-${id}-${displayLabel}`}
                    label={displayLabel}
                    subOptions={subOptions}
                  />
                );
              }
              case 'nested-option': {
                // this nested option is a design/product driven UX where the parent
                // and nested options are both selectable -- this isn't supported
                // by the native html select. contrasting this from the optgroup
                // where the 'parent' with optgroups are _NOT_ selectable
                const {
                  value: nestedOptionValue,
                  subOptions: nestedSubOptions,
                } = option as NestedOptionType;
                return (
                  <Fragment
                    key={`select-nested-option-${id}-${nestedOptionValue}-${displayLabel}`}
                  >
                    <Option value={nestedOptionValue} label={displayLabel} />
                    {nestedSubOptions.map(subOption => {
                      const {
                        label: subOptionLabel,
                        value: subOptionValue,
                      } = subOption;
                      return (
                        <Option
                          key={`select-sub-option-${id}-${nestedOptionValue}-${displayLabel}-${subOptionValue}`}
                          value={subOptionValue}
                          label={subOptionLabel}
                          isNestedSubOption={true}
                        />
                      );
                    })}
                  </Fragment>
                );
              }
              default: {
                return null;
              }
            }
          })}
      </FormSelect>
    );
  }
);

function OptGroup({ label, subOptions }: OptGroupType) {
  return (
    <optgroup label={label}>
      {subOptions.map(({ label: optionLabel, value }) => (
        <Option
          key={`optgroup-option-${value}`}
          value={value}
          label={optionLabel}
        />
      ))}
    </optgroup>
  );
}

function Option({
  label,
  value,
  isNestedSubOption = false,
}: OptionType & { isNestedSubOption?: boolean }) {
  // this is a hacky solution to be able to visually indent a child
  // option for nested-options
  return isNestedSubOption ? (
    // adding the `&nbsp;` will give the illusion that these options are nested
    <option value={value} data-nested-option="true">
      &nbsp;&nbsp;&nbsp;{label}
    </option>
  ) : (
    <option value={value}>{label}</option>
  );
}

function getOptionType(
  option: OptionType | OptGroupType | NestedOptionType
): Variant {
  // regular option - no sub-options
  if (!Object.prototype.hasOwnProperty.call(option, 'subOptions')) {
    return 'option';
  }
  // optgroup - has sub-options, but no value
  // nested option - has sub-options and a value
  return Object.prototype.hasOwnProperty.call(option, 'value')
    ? 'nested-option'
    : 'optgroup';
}

export const ControlledFieldSelect = (props: ControlledFieldSelectProps) => (
  <FieldController<FieldSelectProps, HTMLSelectElement>
    as={FieldSelect}
    {...props}
  />
);
